import { useId, useState } from 'react';
import { Download } from 'lucide-react';
import { useT } from '../../i18n';
import { downloadMyData } from '../../lib/api/personalData';
import { safeId } from '../ui/labelledControl';

// ═══════════════════════════════════════════════════════════════════
// «LAST NED MINE DATA» — THE PERSON'S OWN EXPORT (#1062).
//
// The access and portability right (PERSONAL_DATA.md § 3.1): one JSON file of
// what StarPoint holds about the person, from `GET /me/data-export`. The screen
// says what is in it and — as plainly — what is not: each club's own records
// about the person are the club's, and the file names the clubs instead.
//
// A FRESH SIGN-IN: the server wants one from the last fifteen minutes; an older
// one is answered `reauthentication_required`, the transport opens the step-up
// prompt, and the person presses again.
// ═══════════════════════════════════════════════════════════════════

export interface DataExportSectionProps {
  /** Injectable for tests. */
  download?: typeof downloadMyData;
}

type State =
  { kind: 'idle' } | { kind: 'working' } | { kind: 'done' } | { kind: 'failed'; code: string };

export function DataExportSection({ download = downloadMyData }: DataExportSectionProps) {
  const t = useT();
  const titleId = `${safeId('data-export', useId())}-title`;
  const [state, setState] = useState<State>({ kind: 'idle' });

  const failure = (code: string): string => {
    if (code === 'reauthentication_required') return t('account.export.reauth');
    if (code === 'unreachable') return t('account.export.unreachable');
    return t('account.export.failed');
  };

  const run = async () => {
    setState({ kind: 'working' });
    const res = await download();
    setState(res.code === null ? { kind: 'done' } : { kind: 'failed', code: res.code });
  };

  return (
    <section className="card p-5" aria-labelledby={titleId} data-testid="data-export">
      <h2
        id={titleId}
        className="font-display text-lg font-bold text-ink inline-flex items-center gap-2"
      >
        <Download size={18} aria-hidden="true" /> {t('account.export.title')}
      </h2>
      <p className="mt-1 text-sm text-ink-2">{t('account.export.lead')}</p>
      <p className="mt-1 text-xs text-ink-3">{t('account.export.notIncluded')}</p>
      <div className="mt-3">
        <button
          type="button"
          className="btn-outline"
          disabled={state.kind === 'working'}
          onClick={() => void run()}
          data-testid="data-export-start"
        >
          {state.kind === 'working' ? t('account.export.working') : t('account.export.start')}
        </button>
      </div>
      {state.kind === 'done' && (
        <p className="mt-2 text-sm text-ink" role="status">
          {t('account.export.done')}
        </p>
      )}
      {state.kind === 'failed' && (
        <p className="mt-2 text-sm text-rose-700" role="alert">
          {failure(state.code)}
        </p>
      )}
    </section>
  );
}
